"use client";

import MediaImage from "@/components/ui/MediaImage";
import React from "react";
import * as motion from "motion/react-client";
import { useLookSystem } from "@/components/motion/LookMotion";

interface ZoomImageProps {
  src?: string;
  alt?: string;
  children?: React.ReactNode;
  onClick?: () => void;
  interactive?: boolean;
}

const ZoomImage: React.FC<ZoomImageProps> = ({
  src,
  alt,
  children,
  onClick,
  interactive = true,
}) => {
  const lookSystem = useLookSystem();
  if (!src) return null;
  const className = interactive
    ? "cursor-pointer w-full bg-gray-200 relative"
    : "w-full bg-gray-200 relative";
  const photo = (
    <MediaImage
      {...(lookSystem ? { "data-re-photo": "" } : {})}
      src={src}
      alt={alt ? alt : "image"}
      className="object-cover h-full w-full absolute inset-0"
    />
  );
  // On the look system a photo moves only as its section's level has photos move (src/styles/motion.css).
  if (lookSystem)
    return interactive ? (
      <div
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(event) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          onClick?.();
        }}
        className={className}
      >
        {photo}
        {children}
      </div>
    ) : (
      <div className={className}>
        {photo}
        {children}
      </div>
    );
  return (
    <motion.div
      onClick={interactive ? onClick : undefined}
      initial={{ opacity: 0.5 }}
      animate={{ opacity: 1 }}
      className={className}
      transition={{ duration: 0.3, ease: "easeOut" }}
      whileHover={interactive ? { scale: 1.1 } : undefined}
      whileTap={interactive ? { scale: 0.9 } : undefined}
    >
      {photo}
      {children}
    </motion.div>
  );
};

export default ZoomImage;
